import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import "../components/styles/CartPage.css";
import CartItem from "../components/CartItem";
import CartEmpty from "../components/CartEmpty";
import { useNavigate } from "react-router-dom";
import { useLoggedInUserId } from "../api/GetLoggedInUserId";
import DeleteModal from "../components/cart/DeleteModal";

const CartPage = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { data: memberId, isLoading, isError } = useLoggedInUserId();

    const [cartItems, setCartItems] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [modal, setModal] = useState({ isOpen: false, title: "", message: "", confirmText: "", onConfirm: null });

    // ✅ 장바구니 조회 Mutation
    const fetchCartMutation = useMutation({
        mutationFn: async () => {
            if (!memberId) throw new Error("로그인 필요");
            const response = await fetch(`/api/wishlist/${memberId}`);
            const data = await response.json();
            if (data.statusCode === 200 && data.data) {
                return data.data.map(item => ({
                    id: item.itemId,
                    title: item.itemName,
                    quantity : item.count,
                    subscriptionPeriod: item.subscriptionPeriod,
                    price: item.salePrice,
                    discount: `${item.discountRate}%`,
                    imageUrl: item.itemImagePath || "images/default_product.png"
                }));
            }
            return [];
        },
        onSuccess: (data) => {
            queryClient.setQueryData(["cartItems"], data);
            setCartItems(data);
            setSelectedItems(data.map(item => item.id));
        },
        onError: () => {
            setModal({
                isOpen: true,
                title: "장바구니 오류",
                message: "장바구니 정보를 가져오는 데 실패했습니다.\n새로고침 후 다시 시도해주세요.",
                confirmText: "확인",
                onConfirm: null
            });
        }
    });

    // ✅ 개별 항목 삭제 Mutation
    const deleteItemMutation = useMutation({
        mutationFn: async (id) => {
            const response = await fetch(`/api/wishlist/delete-one/${id}?memberId=${memberId}`, { method: "GET" });
            const data = await response.json();
            if (data.statusCode !== 200) throw new Error("삭제 실패");
            return id;
        },
        onSuccess: (id) => {
            setCartItems(prev => prev.filter(item => item.id !== id));
            setSelectedItems(prev => prev.filter(item => item !== id));
            setModal({
                isOpen: true,
                title: "해당 상품 삭제 완료",
                message: "선택한 상품이 장바구니에서 삭제되었습니다.",
                confirmText: "확인",
                onConfirm: null
            });
            // queryClient.invalidateQueries(["cartItems"]);
        },
        onError: () => {
            setModal({
                isOpen: true,
                title: "삭제 오류",
                message: "상품 삭제에 실패했습니다. 다시 시도해주세요.",
                confirmText: "확인",
                onConfirm: null
            });
        }
    });

    React.useEffect(() => {
        if (memberId) fetchCartMutation.mutate();
    }, [memberId]);

    // ✅ 삭제 모달 오픈 및 실행
    const handleDeleteItem = (id) => {
        setModal({
            isOpen: true,
            title: "상품 삭제 확인",
            message: "해당 상품을 장바구니에서 삭제하시겠습니까?",
            confirmText: "삭제",
            onConfirm: () => deleteItemMutation.mutate(id)
        });
    };

    const handleDeleteSelected = () => {
        setModal({
            isOpen: true,
            title: "선택한 상품 삭제 확인",
            message: "선택한 상품을 장바구니에서 삭제하시겠습니까?",
            confirmText: "삭제",
            onConfirm: () => {
                selectedItems.forEach(id => deleteItemMutation.mutate(id));
                setSelectedItems([]);
            }
        });
    };

    // ✅ 총 결제 금액 계산
    const totalAmount = cartItems
        .filter(item => selectedItems.includes(item.id))
        .reduce((acc, item) => acc + item.price, 0);

    // ✅ 주문서 생성 Mutation
    const createOrderMutation = useMutation({
        mutationFn: async () => {
            if (!memberId) throw new Error("로그인 필요");

            const orderData = {
                memberId,
                orderType: "NEW",
                items: cartItems.filter(item => selectedItems.includes(item.id)).map(item => ({
                    itemId: item.id,
                    month: item.subscriptionPeriod,
                    quantity: item.quantity,
                })),
            };

            console.log(orderData.items);

            const response = await fetch(`/api/order`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(orderData),
            });

            const data = await response.json();
            if (data.result !== "available") throw new Error("주문서 생성 실패");

            console.log("주문서 ID : ", data);
            return data; // ✅ 생성된 주문서 ID 반환
        },
        onSuccess: (data) => {
            console.log("주문서 생성 완료");
            navigate(`/order/checkout`, { state: { data } });
        },
        onError: () => {
            setModal({
                isOpen: true,
                title: "주문 오류",
                message: "주문서를 생성하는 데 실패했습니다. 다시 시도해주세요.",
                confirmText: "확인",
                onConfirm: null
            });
        }
    });

    // ✅ 결제 진행
    const handleCheckout = () => {
        if (selectedItems.length === 0) {
            alert("결제할 상품을 선택해주세요.");
            return;
        }
        createOrderMutation.mutate(); // ✅ 주문서 생성 후 결제 페이지로 이동
    };

    if (isLoading) return <p>로딩 중...</p>;
    if (isError || !memberId) return <p>로그인이 필요합니다.</p>;
    if (cartItems.length === 0) return <CartEmpty />;

    return (
        <div className="cart-page">
            <h1 className="cart-title">장바구니</h1>
            <div className="cart-header">
                <span className="delete-all" onClick={handleDeleteSelected}>
                    선택삭제
                </span>
            </div>

            <div className="cart-items">
                {cartItems.map(item => (
                    <CartItem
                        key={item.id}
                        item={item}
                        selected={selectedItems.includes(item.id)}
                        onSelect={(id) => setSelectedItems(prev =>
                            prev.includes(id) ? prev.filter(itemId => itemId !== id) : [...prev, id]
                        )}
                        onDelete={handleDeleteItem} // ✅ 삭제 요청을 모달을 통해 실행
                    />
                ))}
            </div>

            {/* ✅ 팝업에서 직접 삭제 요청 실행 */}
            <DeleteModal
                isOpen={modal.isOpen}
                title={modal.title}
                message={modal.message}
                confirmText={modal.confirmText}
                onConfirm={modal.onConfirm}
                onClose={() => setModal({ isOpen: false })}
            />

            <div className="cart-footer">
                <p>주문 내용을 확인하였으며, 정보 제공 등에 동의합니다</p>
                {/* ✅ 버튼 클릭 시 선택된 아이템을 가지고 결제 페이지로 이동 */}
                <button onClick={handleCheckout}>{totalAmount.toLocaleString()}원 결제</button>
            </div>
        </div>
    );
};

export default CartPage;